import { DlInstance } from '@/lib/axios'
import { storage } from '@/lib/storage'
import type { LiffProfile } from '@/lib/liff'

export type LiffAuthUser = {
  id: string
  firstname?: string | null
  lastname?: string | null
  role?: { key?: string; name?: string }
  status?: string
}

export type LiffAuthTokens = {
  user: LiffAuthUser
  accessToken: string
  refreshToken: string
}

function persistAuth(data: LiffAuthTokens) {
  storage.set('accessToken', data.accessToken)
  storage.set('refreshToken', data.refreshToken)
  const roleValue =
    typeof data.user.role === 'object'
      ? data.user.role?.key || data.user.role?.name
      : data.user.role
  if (roleValue) {
    storage.set('userRole', roleValue)
  }
}

export async function checkLiffBinding(profile: Pick<LiffProfile, 'userId' | 'idToken'>) {
  const { data } = await DlInstance.post<{ data: { bound: boolean; line_user_id: string } }>(
    '/auth/liff/binding-status',
    {
      line_user_id: profile.userId,
      id_token: profile.idToken,
    },
  )
  return data.data
}

export async function liffSignIn(profile: Pick<LiffProfile, 'userId' | 'idToken'>): Promise<LiffAuthTokens> {
  const { data } = await DlInstance.post<{
    data: LiffAuthTokens
  }>('/auth/liff/signin', {
    line_user_id: profile.userId,
    id_token: profile.idToken,
  })
  const tokens = data.data
  persistAuth(tokens)
  return tokens
}

export async function liffBind(
  profile: Pick<LiffProfile, 'userId' | 'idToken' | 'displayName'>,
): Promise<void> {
  await DlInstance.post('/auth/liff/bind', {
    line_user_id: profile.userId,
    id_token: profile.idToken,
    display_name: profile.displayName,
  })
}

export async function credentialSignInAndBind(
  username: string,
  password: string,
  profile: Pick<LiffProfile, 'userId' | 'idToken' | 'displayName'>,
): Promise<LiffAuthTokens> {
  const signInRes = await DlInstance.post<{
    data: LiffAuthTokens
  }>('/auth/signin', { username, password })

  const tokens = signInRes.data.data
  persistAuth(tokens)

  await liffBind(profile)
  return tokens
}

export function clearAuthStorage() {
  storage.remove('accessToken')
  storage.remove('refreshToken')
  storage.remove('userRole')
}

export function hasAuthTokens(): boolean {
  return Boolean(storage.get('accessToken') && storage.get('refreshToken'))
}
